import React from 'react';
import { Link } from 'react-router-dom';
import { City } from '@/data/cities';
import { CitySpecialty, citySpecialties } from '@/data/citySpecialties';
import { CityLanguage } from '@/context/languages/languageData';
import { getLocalizedSpecialtyPath } from '@/utils/cityLanguage/getLocalizedSpecialtyPath';
import { getSpecialtyNameForLanguage } from '@/utils/cityLanguage/getSpecialtySlugForLanguage';
import * as Icons from 'lucide-react';

interface RelatedSpecialtiesProps {
  city: City;
  currentSpecialty: CitySpecialty;
  language: CityLanguage;
}

const RelatedSpecialties: React.FC<RelatedSpecialtiesProps> = ({ 
  city, 
  currentSpecialty,
  language 
}) => {
  // Get related specialties (exclude current, show max 6)
  const relatedSpecialties = citySpecialties
    .filter(s => s.id !== currentSpecialty.id)
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 6);

  const title = language === 'es' 
    ? 'Otras Especialidades Disponibles'
    : 'Other Available Specialties';

  return (
    <section className="py-12 bg-secondary/5">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-8">{title}</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {relatedSpecialties.map((specialty) => {
            const IconComponent = (Icons as any)[specialty.icon] || Icons.User;
            const specialtyName = getSpecialtyNameForLanguage(specialty, language);
            const specialtyPath = getLocalizedSpecialtyPath(city, specialty, language);

            return (
              <Link
                key={specialty.id}
                to={specialtyPath}
                className="group bg-card hover:bg-accent/10 border border-border hover:border-primary/50 rounded-lg p-4 transition-all duration-300 hover:shadow-md"
              >
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="p-3 bg-primary/10 rounded-full group-hover:bg-primary/20 transition-colors">
                    <IconComponent className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">
                    {specialtyName}
                  </h3>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RelatedSpecialties;
